import type { HttpError } from '@sveltejs/kit';
import { derived, writable } from 'svelte/store';

// AppError extends the Error class
export class AppError extends Error {
	code: number;
	cause: any;
	constructor(code: number, message: string, cause?: any) {
		super(message);
		this.code = code;
		this.cause = cause;
	}

	toString() {
		let str = `${this.name}(${this.code}): ${this.message}`;
		if (!this.cause) {
			return str;
		}
		str += `\nCaused by: `;
		if (typeof this.cause === 'string') str += this.cause;
		else if (this.cause instanceof Error) return (str += this.cause.toString());
		else if (Object.hasOwn(this.cause, 'message')) str += this.cause.message;
		else if (this.cause.toString() != '[object Object]') str += this.cause.toString();
		else str += JSON.stringify(this.cause, null, 2);
		return str;
	}
}

// It accumulates global errors to be displayed in the UI,
// It is cleared when the user navigates to a different page or when explicitly cleared.
const errorStore = writable<AppError[]>([]);

export default {
	firstError: derived(errorStore, ($err) => ($err.length > 0 ? $err[0] : null)),
	otherErrors: derived(errorStore, ($err) => ($err.length > 1 ? $err.slice(1) : [])),
	addError(err: AppError | HttpError | Error | string) {
		let appErr: AppError;
		// Convert if necessary
		if (!err) {
			appErr = new AppError(500, 'Unknown error');
		} else if (err instanceof AppError) {
			appErr = err;
		} else if (err instanceof Error) {
			appErr = new AppError(500, err.message);
		} else if (typeof err === 'string') {
			appErr = new AppError(500, err);
		} else {
			const code = err.status || 500;
			let msg = 'Unknown error';
			if (err.body) {
				if (Object.hasOwn(err.body, 'message')) {
					msg = err.body.message;
				} else {
					msg = JSON.stringify(err.body);
				}
			}
			appErr = new AppError(code, msg);
		}
		errorStore.update((errs) => [...errs, appErr]);
	},
	clearError() {
		errorStore.set([]);
	},
};
